import React, { useCallback, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import Alert from '@amzn/awsui-components-console/alert';
import Box from '@amzn/awsui-components-console/box';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import NamespaceHeader from './components/NamespaceHeader';
import StringGroupsTable from './components/StringGroupsTable';
import CreateStringGroupModal from './components/CreateStringGroupModal';
import { useNamespacePageData } from './hooks/useNamespacePageData';
import { useStringGroupsData } from './hooks/useStringGroupsData';
import Skeleton from '@amzn/awsui-components-console/skeleton';
import StackedBreadcrumbGroup from 'src/common/ui/StackedBreadcrumbGroup';
import { useNamespacePermissions } from 'src/common/hooks/useNamespacePermissions';
import { CreateStringGroupFormData } from './types';
import type { NewStringGroupMetadata } from 'src/features/string-group-detail/types';
import { getUsernameFromJwt } from 'src/common/utils/auth';

const NamespaceDetailPage: React.FC = () => {
  const { namespaceId } = useParams<{ namespaceId: string }>();
  const navigate = useNavigate();
  const pageData = useNamespacePageData(namespaceId);
  const stringGroupsData = useStringGroupsData(namespaceId);
  const permissions = useNamespacePermissions(namespaceId);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const openCreateModal = useCallback(() => {
    setShowCreateModal(true);
  }, []);

  const handleCreateStringGroup = useCallback(
    async (data: CreateStringGroupFormData): Promise<void> => {
      // TODO: Wire the create mutation (e.g. useCreateStringGroupMutation).
      // Nothing is persisted yet — the string group is NOT created on the
      // backend here, so the form's description, teamName, cxFamilyId and
      // sourceLocale survive only in the navigation state handed over below and
      // are lost on refresh. Creation is deferred until that mutation exists.
      // await createStringGroupMutation({ variables: { input: { ... } } });

      // Redirect to the (not yet persisted) group's detail page, carrying the
      // form data in navigation state. The detail page renders its metadata
      // from this handoff instead of fetching describeStringGroup, which has
      // nothing to return for a group the backend does not know about yet.
      const newGroupMetadata: NewStringGroupMetadata = {
        stringGroupId: data.name,
        namespaceId: namespaceId ?? '',
        description: data.description,
        teamName: data.teamName,
        cxFamilyId: data.cxFamilyId,
        // Flatten Cloudscape option objects to plain values: navigation state
        // must be structured-clone serializable, and the detail page only
        // needs the ids.
        cxIds: data.cxIds
          .map((option) => option.value ?? '')
          .filter((value) => value !== ''),
        sourceLocale: data.sourceLocale?.value ?? '',
        createdOn: Date.now(),
        // Same source of truth AppHeader uses for the avatar: the Midway
        // alias from the JWT. Lets the prefilled metadata panel render
        // "18/08/2026 by buyyala" without a backend round trip.
        createdBy: getUsernameFromJwt() ?? '',
      };
      navigate(
        `/${encodeURIComponent(namespaceId ?? '')}/${encodeURIComponent(data.name)}`,
        {
          state: { newStringGroup: newGroupMetadata },
        },
      );
    },
    [namespaceId, navigate],
  );

  return (
    <SpaceBetween size="l">
      <StackedBreadcrumbGroup
        items={[{ topText: 'NAMESPACE', bottomText: namespaceId ?? '' }]}
      />

      {pageData.namespaceError ? (
        <Alert type="error" header="Failed to load namespace">
          {pageData.namespaceError.message}
        </Alert>
      ) : pageData.namespaceLoading ? (
        <Box textAlign="center" padding="l">
          <Skeleton />
        </Box>
      ) : (
        <NamespaceHeader
          namespace={pageData.namespace}
          onCreateStringGroup={openCreateModal}
          canCreateStringGroup={permissions.canManageSource}
          permissionsLoading={permissions.loading}
        />
      )}

      {stringGroupsData.error && (
        <Alert type="error" header="Failed to load string groups">
          {stringGroupsData.error.message}
        </Alert>
      )}

      {!pageData.namespaceError && !stringGroupsData.error && (
        <StringGroupsTable
          namespaceId={namespaceId ?? ''}
          displayItems={stringGroupsData.displayItems}
          loading={stringGroupsData.initialLoading}
          isLoadingMore={stringGroupsData.isLoadingMore}
          hasMore={stringGroupsData.hasMore}
          handleLoadMore={stringGroupsData.handleLoadMore}
          hideCxFamilyColumn={!!pageData.namespace?.defaultCxFamily}
        />
      )}

      <CreateStringGroupModal
        visible={showCreateModal}
        onDismiss={() => setShowCreateModal(false)}
        onSubmit={handleCreateStringGroup}
        cxFamily={pageData.namespace?.defaultCxFamily}
      />
    </SpaceBetween>
  );
};

export default NamespaceDetailPage;
